import useViewerRole from "@/lib/rights/use-viewer-role";
import useTranslation from "@babackapp/translations/use-translation";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@babackapp/ui/components/hover-card";
import { cn } from "@babackapp/ui/lib/utils";
import { BoxIcon } from "@radix-ui/react-icons";
import { Check, CheckCheck, Truck, XIcon } from "lucide-react";

type IconContainerProps = {
  className: string;
  children: React.ReactNode;
};

const IconContainer = ({ className, children }: IconContainerProps) => (
  <div
    className={cn(
      "rounded-full size-6 flex items-center justify-center",
      className,
    )}
  >
    {children}
  </div>
);

type StatusType =
  | "ready-for-merchant"
  | "ready-for-logistician"
  | "treated-by-merchant"
  | "rejected-by-merchant"
  | "validated-by-logistician"
  | "rejected-by-logistician"
  | "not-returned";

type IconProps = {
  status: StatusType;
};

function Icon({ status }: IconProps) {
  const role = useViewerRole();

  let icon = null;

  if (role === "logistician") {
    switch (status) {
      case "ready-for-logistician":
        icon = (
          <IconContainer className="bg-secondary">
            <Truck className="text-secondary-foreground size-4" />
          </IconContainer>
        );
        break;
      case "validated-by-logistician":
        icon = (
          <IconContainer className="bg-green-500">
            <Check className="text-white size-4" />
          </IconContainer>
        );
        break;
      case "treated-by-merchant":
      case "ready-for-merchant":
        icon = (
          <IconContainer className="bg-green-500">
            <CheckCheck className="text-white size-4" />
          </IconContainer>
        );
        break;
      case "rejected-by-merchant":
      case "rejected-by-logistician":
        icon = (
          <IconContainer className="bg-red-500">
            <XIcon className="text-white size-4" />
          </IconContainer>
        );
        break;
      default:
        icon = null;
    }
  } else {
    switch (status) {
      case "ready-for-merchant":
        icon = (
          <IconContainer className="bg-orange-500">
            <BoxIcon className="text-white size-4" />
          </IconContainer>
        );
        break;
      case "ready-for-logistician":
        icon = (
          <IconContainer className="bg-secondary">
            <Truck className="text-secondary-foreground size-4" />
          </IconContainer>
        );
        break;
      case "treated-by-merchant":
        icon = (
          <IconContainer className="bg-green-500">
            <CheckCheck className="text-white size-4" />
          </IconContainer>
        );
        break;
      case "rejected-by-merchant":
        icon = (
          <IconContainer className="bg-red-500">
            <XIcon className="text-white size-4" />
          </IconContainer>
        );
        break;
      case "rejected-by-logistician":
        icon = (
          <IconContainer className="bg-red-500">
            <BoxIcon className="text-white size-4" />
          </IconContainer>
        );
        break;
      case "validated-by-logistician":
        icon = (
          <IconContainer className="bg-orange-500">
            <Check className="text-white size-4" />
          </IconContainer>
        );
        break;
      default:
        icon = null;
    }
  }

  return icon;
}

export type ArticleItemStatusProps = {
  status: StatusType;
};

export const ArticleItemStatus = ({ status }: ArticleItemStatusProps) => {
  const role = useViewerRole() === "logistician" ? "logistician" : "merchant";
  const t = useTranslation();

  // A logistician looking at a merchant-rejected article should read it as a
  // rejection, not as the generic "processed" wording of the shared key.
  const keyBase =
    role === "logistician" && status === "rejected-by-merchant"
      ? "lineItemStatus.logistician.merchant-rejected"
      : `lineItemStatus.${role}.${status}`;

  const title = t(`${keyBase}.title` as Parameters<typeof t>[0]);
  const description = t(`${keyBase}.description` as Parameters<typeof t>[0]);

  return (
    <HoverCard>
      <HoverCardTrigger>
        <Icon status={status} />
      </HoverCardTrigger>
      <HoverCardContent>
        <span className="text-sm font-medium text-muted-foreground">
          {title}
        </span>
        <p className="text-xs text-muted-foreground">{description}</p>
      </HoverCardContent>
    </HoverCard>
  );
};
